/**
 * Server-rendered app shell shown while the client-only provider tree mounts
 * (see `app/providers.tsx`). Replaces the previous `return null`, which left the
 * page blank from the SSR HTML until hydration completed — the single biggest
 * perceived-performance issue of the lift-and-shift Next.js migration.
 *
 * Pure HTML/CSS only: the `ChakraProvider` lives *inside* the mount gate, so no
 * Chakra component, theme token, or browser API may be used here. Colors are
 * hardcoded to mirror the real chrome (cream page bg `#FFFCEF`, white header,
 * `gray.200` border) with a `html.dark` override — next-themes sets that class
 * synchronously before first paint, so the skeleton paints in the right theme.
 *
 * Structurally generic (fixed 80px header + centered content shimmer) so it fits
 * every route — main, public and admin — without reading the pathname. The first
 * client render also returns this (mounted === false), matching the SSR output,
 * so there is no hydration mismatch.
 */
export const AppSkeleton = () => (
  <div className="cmn-skel" aria-hidden="true" aria-busy="true">
    <style>{SKELETON_CSS}</style>

    <header className="cmn-skel__header">
      <div className="cmn-skel__container cmn-skel__header-row">
        <div className="cmn-skel__logo cmn-skel__shimmer" />
        <div className="cmn-skel__search cmn-skel__shimmer" />
        <div className="cmn-skel__actions">
          <div className="cmn-skel__dot cmn-skel__shimmer" />
          <div className="cmn-skel__avatar cmn-skel__shimmer" />
        </div>
      </div>
    </header>

    <main className="cmn-skel__main">
      <div className="cmn-skel__container">
        <div className="cmn-skel__title cmn-skel__shimmer" />
        <div className="cmn-skel__subtitle cmn-skel__shimmer" />
        <div className="cmn-skel__cards">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="cmn-skel__card cmn-skel__shimmer" />
          ))}
        </div>
      </div>
    </main>
  </div>
)

const SKELETON_CSS = `
.cmn-skel { min-height: 100vh; background: #FFFCEF; }
html.dark .cmn-skel { background: #374151; }

.cmn-skel__container { max-width: 1280px; margin: 0 auto; padding-left: 16px; padding-right: 16px; }

.cmn-skel__header {
  position: fixed; top: 0; left: 0; width: 100%; height: 80px; z-index: 1000;
  display: flex; align-items: center;
  background: #fff; border-bottom: 1px solid #E2E8F0;
}
html.dark .cmn-skel__header { background: #111827; border-bottom-color: #374151; }

.cmn-skel__header-row { display: flex; align-items: center; gap: 24px; width: 100%; }
.cmn-skel__logo { width: 140px; height: 28px; border-radius: 6px; flex: none; }
.cmn-skel__search { flex: 1; max-width: 420px; height: 40px; border-radius: 20px; }
.cmn-skel__actions { margin-left: auto; display: flex; align-items: center; gap: 14px; flex: none; }
.cmn-skel__dot { width: 28px; height: 28px; border-radius: 50%; }
.cmn-skel__avatar { width: 40px; height: 40px; border-radius: 50%; }

.cmn-skel__main { padding-top: 112px; padding-bottom: 48px; }
.cmn-skel__title { width: 240px; max-width: 60%; height: 32px; border-radius: 8px; margin-bottom: 16px; }
.cmn-skel__subtitle { width: 360px; max-width: 80%; height: 18px; border-radius: 6px; margin-bottom: 32px; }
.cmn-skel__cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 20px; }
.cmn-skel__card { height: 200px; border-radius: 12px; }

@media (max-width: 768px) {
  .cmn-skel__search { display: none; }
}

.cmn-skel__shimmer { background: #E2E8F0; position: relative; overflow: hidden; }
html.dark .cmn-skel__shimmer { background: #4B5563; }
.cmn-skel__shimmer::after {
  content: ""; position: absolute; inset: 0; transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent);
  animation: cmn-skel-sweep 1.4s ease-in-out infinite;
}
html.dark .cmn-skel__shimmer::after {
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent);
}
@keyframes cmn-skel-sweep { 100% { transform: translateX(100%); } }
@media (prefers-reduced-motion: reduce) { .cmn-skel__shimmer::after { animation: none; } }
`
